import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import { DEFAULT_THEME, ThemeMode } from '@apps-common/config/theme'
import { hydrateTheme } from '@apps-common/theme/hydrateTheme'
import type { PortalThemeModel, ThemePalette, ThemePayload } from '@apps-common/types/portal/theme'

type PaletteMode = 'light' | 'dark'
type ThemePaletteKey = keyof ThemePalette

interface IThemeStore {
  initializing: boolean

  open: boolean
  setOpen: (open: boolean) => void

  editing: boolean
  toggleEditing: () => void

  themeMode: ThemeMode
  setThemeMode: (value: ThemeMode) => void

  companyId: string | undefined
  setCompanyId: (value: string | undefined) => void

  contextTheme: PortalThemeModel | null
  setContextTheme: (value: PortalThemeModel | null) => void

  /** The currently applied theme (usually fetched from API) */
  theme: PortalThemeModel
  setTheme: (value: PortalThemeModel) => void

  /** Theme being edited in the UI */
  modifiedTheme: PortalThemeModel | null

  /**
   * Update a palette value (ex: light.bg, dark.brand, etc)
   */
  setModifiedPaletteValue: (mode: PaletteMode, key: ThemePaletteKey, value: string) => void

  /**
   * Update a top-level theme field (rare; mostly for admin metadata)
   */
  setModifiedThemeField: (key: keyof PortalThemeModel, value: any) => void

  /**
   * Reset edits back to saved theme (from API)
   */
  resetModifiedTheme: () => void

  /**
   * Reset edits back to DEFAULT_THEME (factory defaults)
   * Does NOT automatically overwrite the saved theme on the backend.
   */
  resetModifiedThemeToDefault: () => void

  /**
   * Optional helper:
   * Immediately apply DEFAULT_THEME as the app theme + editor theme.
   * (Still does NOT save to backend unless you call your save mutation.)
   */
  resetThemeToDefaultAndApply: () => void
}

const ensurePalettes = (t?: PortalThemeModel | null): PortalThemeModel => {
  const hydrated = hydrateTheme(t ?? DEFAULT_THEME)

  // safety: palettes should always exist after hydrateTheme
  if (!hydrated.palettes) {
    return { ...hydrated, palettes: DEFAULT_THEME.palettes }
  }
  return hydrated
}

const clonePayload = (p: ThemePayload): ThemePayload => ({
  light: { ...p.light },
  dark: { ...p.dark }
})

export const useThemeStore = create<IThemeStore, [['zustand/persist', unknown]]>(
  persist(
    (set, get) => ({
      initializing: true,

      open: false,
      setOpen: (open: boolean) => set({ open }),

      editing: false,
      toggleEditing: () => set({ editing: !get().editing }),

      themeMode: ThemeMode.system,
      setThemeMode: (value: ThemeMode) => set({ themeMode: value }),

      companyId: undefined,
      setCompanyId: (value: string | undefined) => set({ companyId: value }),

      contextTheme: null,
      setContextTheme: (value: PortalThemeModel | null) => set({ contextTheme: value }),

      theme: DEFAULT_THEME,
      setTheme: (value: PortalThemeModel) => {
        const hydrated = ensurePalettes(value)
        set({ theme: hydrated, modifiedTheme: hydrated })
      },

      modifiedTheme: null,

      setModifiedPaletteValue: (mode: PaletteMode, key: ThemePaletteKey, value: string) => {
        const { theme, modifiedTheme } = get()
        const base = ensurePalettes(modifiedTheme ?? theme)

        const palettes: ThemePayload = base.palettes
          ? clonePayload(base.palettes as ThemePayload)
          : (DEFAULT_THEME.palettes as ThemePayload)

        // Update the specific value
        palettes[mode] = { ...palettes[mode], [key]: value }

        const next: PortalThemeModel = {
          ...base,
          palettes,
          palettesJson: JSON.stringify(palettes)
        }

        set({ modifiedTheme: next })
      },

      setModifiedThemeField: (key: keyof PortalThemeModel, value: any) => {
        const { theme, modifiedTheme } = get()
        const base = ensurePalettes(modifiedTheme ?? theme)

        set({
          modifiedTheme: {
            ...base,
            [key]: value
          }
        })
      },

      // Reset edits back to the *saved* theme
      resetModifiedTheme: () => {
        const { theme } = get()
        const hydrated = ensurePalettes(theme)
        set({ modifiedTheme: hydrated })
      },

      // Reset edits back to *factory defaults*
      resetModifiedThemeToDefault: () => {
        const { theme, modifiedTheme } = get()
        const base = ensurePalettes(modifiedTheme ?? theme) // preserve ids/companyId/etc
        const defaults = ensurePalettes(DEFAULT_THEME)

        const palettes = clonePayload(defaults.palettes as ThemePayload)

        const next: PortalThemeModel = {
          ...base,
          palettes,
          palettesJson: JSON.stringify(palettes)
        }

        set({ modifiedTheme: next })
      },

      // Apply defaults immediately as the app theme + editor theme
      resetThemeToDefaultAndApply: () => {
        const { theme, modifiedTheme } = get()
        const base = ensurePalettes(modifiedTheme ?? theme)
        const defaults = ensurePalettes(DEFAULT_THEME)

        const palettes = clonePayload(defaults.palettes as ThemePayload)

        const next: PortalThemeModel = {
          ...base,
          palettes,
          palettesJson: JSON.stringify(palettes)
        }

        set({ theme: next, modifiedTheme: next })
      }
    }),
    {
      name: 'app-theme',
      partialize: (state) => {
        const { contextTheme, companyId, ...persisted } = state
        return persisted
      }
    }
  )
)
